import { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { useSelector } from 'statux'

import Image from '../../../components/Image/Image'
import Table from '../../../components/Table/Table'

import { getCssVar } from '../../../utils/css'
import { getInitials } from '../../../utils/strings'
import { graphqlClient, gql } from '../../../utils/gql'

import authProvider from '../../../utils/auth-provider'

export default function DashboardEbpc (props = {}) {
    const role = authProvider.getDefaultRole()

    const isMember = role === 'member'

    const isEbpcJudge = role === 'ebpc_judge'

    const isAdmin = role === 'admin'

    const history = useHistory()

    const { location } = history

    const [{ applications, count }, setApplications] = useState({
        ebpcApplication: (history.location.state || {}).ebpcApplications
            || null,
        count: (history.location.state || {}).count
            || null
    })

    const params = useParams()

    const page = parseFloat(params.page || 1)

    const limit = 25

    const offset = (page - 1) * limit

    const {
        ebpcContest,
        ebpcApplicationsOpen
    } = useSelector()

    useEffect(async () => {
        let redirect = '/dashboard'

        if (isMember
                && ebpcApplicationsOpen)
            redirect += '/ebpc/application'

        if (!(ebpcContest || {}).id
                || (!isAdmin
                    && !isEbpcJudge))
            return history.push(redirect)

        const query = await graphqlClient
            .request(gql`
                query {
                    ebpc_applications (
                        limit: ${limit},
                        offset: ${offset},
                        order_by: { submitted_at: desc },
                        where: {
                            ebpc_contest_id: { _eq: ${ebpcContest.id} },
                            submitted_at: { _is_null: false }
                        }
                    ) {
                        id
                        data
                        venture: data(path: "aboutYourVenture.ventureName")
                        profile {
                            company
                            user {
                                id
                                avatar_url
                                display_name
                            }
                        }
                    }

                    ebpc_applications_aggregate (
                        where: {
                            ebpc_contest_id: { _eq: ${ebpcContest.id} },
                            submitted_at: { _is_null: false }
                        }
                    ) {
                        aggregate {
                            count
                        }
                    }
                }`)
            .catch((err) => {
                console.warn(err)

                return ({
                    ebpc_applications: [],
                    ebpc_applications_aggregate: {
                        aggregate: {
                            count: 0
                        }
                    }
                })
            })

        const queryApplications = query.ebpc_applications
            .map(obj => ({
                plan: `24-${`${obj.id}`
                    .padStart(4, '0')}`,
                venture: obj.venture || obj.profile.company,
                applicant: obj.profile.user
            }))

        const queryCount = query.ebpc_applications_aggregate
            .aggregate
            .count

        setApplications({
            applications: queryApplications,
            count: queryCount
        })

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            Object.assign(history.location.state || {}, {
                ebpcApplications: queryApplications,
                count: queryCount
            }))
    }, [params.page])

    const isNoResults = !(applications || []).length
        && applications !== null

    const maxPages = !isNoResults
        && count
        && Math.ceil(count / limit)

    let pages = []

    if (maxPages) {
        let start

        const range = 5

        if (page < (range / 2) + 1)
            start = 1

        if (page >= count - (range / 2))
            start = Math.floor(count - range + 1)

        if (!start)
            start = Math.floor(page - (range / 2))

        if (start < 1)
            start = 1

        Array.from(Array(range))
            .forEach((noop, idx) => idx + start <= maxPages
                && pages.push(idx + start))

        if (pages.length < range
                && maxPages > range)
            pages = [pages[0] - 1].concat(pages)
    }

    const basePathname = (location.pathname || window.location.pathname)
        .replace(/\/page.+/, '')
        .replace(/\/$/, '')

    return (
        <div
            className="dashboard-ebpc pad-3-t pad-4-b pad-4-t-lg-up"
        >
            <div className="container">
                <div className="row-condensed margin-3-b">
                    <div className="col-12">
                        <Link
                            to="/dashboard"
                            className="body-4 display-inline-flex"
                            style={{
                                color: getCssVar('--slate'),
                                marginBottom: '5px',
                                lineHeight: 1.1,
                                overflow: 'hidden'
                            }}
                        >
                            <svg
                                className="icon-chevron-left flex-none"
                                width="18"
                                height="18"
                                style={{ margin: '-2px 2px 0 -5px' }}
                            >
                                <use xlinkHref="#icon-chevron-left" />
                            </svg>
                            <span>
                                <span className="display-none-sm-down">Go back </span>
                                <span className="text-transform-capitalize display-none-sm-up">back </span>
                                to {isEbpcJudge
                                    ? 'EBPC judging portal'
                                    : 'Dashboard'}
                            </span>
                        </Link>
                    </div>
                    <div className="col-12 col-7-lg-up">
                        <h1 className="heading-2 display-flex">
                            All venture submissions
                        </h1>
                    </div>
                </div>

                <div className="row-condensed">
                    <section
                        className={`col-12${isEbpcJudge
                            ? ' col-8-xl-up'
                            : ''}`}
                    >
                        <div className="dashboard-ebpc-applications card card-offscreen-x-lg-down">
                            {!(applications || []).length
                                && (
                                    <>
                                        <h2 className="heading-5 mb-1">No submissions available</h2>
                                        <div className="rich-text body-3">
                                            <p>Please try again later</p>
                                        </div>
                                    </>)}
                            {Boolean((applications || []).length)
                                && (
                                    <Table
                                        style={{
                                            color: getCssVar('--navy')
                                        }}
                                    >
                                        <thead>
                                            <tr>
                                                <th>
                                                    <span>Plan</span>
                                                    <span className="display-none-sm-down"> number</span>
                                                    <span className="display-none-sm-up" aria-label="number"> #</span>
                                                </th>
                                                <th>Venture name</th>
                                                <th>Applicant name</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(applications || [])
                                                .map((obj, idx) => (
                                                    <tr
                                                        key={`dashboard-ebpc-applications-${idx}`}
                                                    >
                                                        <td><span>{obj.plan}</span></td>
                                                        <td
                                                            className="table-col-venture"
                                                        >
                                                            <Link
                                                                to={`/dashboard/ebpc/applications/${obj.applicant.id}`}
                                                                title="View application"
                                                            >
                                                                <span>{obj.venture}</span>
                                                            </Link>
                                                        </td>
                                                        <td
                                                            className="table-col-applicant"
                                                        >
                                                            <Link
                                                                to={`/dashboard/profiles/${obj.applicant.id}`}
                                                                title={`View profile of ${obj.applicant.display_name}`}
                                                                aria-label={obj.applicant.display_name}
                                                            >
                                                                <span className="avatar avatar-sm">
                                                                    <Image
                                                                        src={!(obj.applicant.avatar_url || '')
                                                                            .match(/gravatar\.com/)
                                                                            ? obj.applicant.avatar_url
                                                                            : null}
                                                                        textFallback={getInitials(obj.applicant.display_name)}
                                                                        data-object-fit="cover"
                                                                    />
                                                                </span>
                                                                <span>{obj.applicant.display_name}</span>
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </Table>)}

                            {Boolean(pages.length)
                                && (
                                    <nav
                                        className="pagination container-flex flex-justify-content-center margin-3-t margin-5-t-lg-up"
                                        aria-label="EBPC Application results pages"
                                        style={{
                                            visibility: (applications || []).length
                                                ? 'visible'
                                                : 'hidden'
                                        }}
                                    >
                                        <Link
                                            to={`${basePathname}${
                                                params.page > 2
                                                    ? `/page/${page - 1}`
                                                    : ''}${
                                                location.search}`}
                                            className="pagination-prev flex-none"
                                            aria-label="Go to previous page"
                                            aria-disabled={page === 1}
                                        >
                                            <svg
                                                className="flex-none icon-chevron-left"
                                                width="24"
                                                height="24"
                                                style={{ maxHeight: '24px' }}
                                            >
                                                <use xlinkHref="#icon-chevron-left" />
                                            </svg>
                                        </Link>
                                        <ul className="display-flex flex-wrap">
                                            {pages
                                                .map((idx) => {
                                                    return (
                                                        <li
                                                            key={`dashboard-ebpc-pagination-${idx}`}
                                                            className="flex-none"
                                                        >
                                                            <Link
                                                                to={`${basePathname}${
                                                                    idx > 1
                                                                        ? `/page/${idx}`
                                                                        : ''}${
                                                                    location.search}`}
                                                                className="text-decoration-hover"
                                                                aria-label={`Page ${idx}`}
                                                                aria-current={page === idx
                                                                    && 'page'}
                                                            >
                                                                <span>{idx}</span>
                                                            </Link>
                                                        </li>
                                                    )
                                                })}
                                        </ul>
                                        <Link
                                            to={`${basePathname}${
                                                page < maxPages
                                                    ? `/page/${page + 1}`
                                                    : ''}${
                                                location.search}`}
                                            className="pagination-next flex-none"
                                            role="link"
                                            aria-label="Go to next page"
                                            aria-disabled={page === maxPages}
                                        >
                                            <svg
                                                className="flex-none icon-chevron-right"
                                                width="24"
                                                height="24"
                                                style={{ maxHeight: '24px' }}
                                            >
                                                <use xlinkHref="#icon-chevron-right" />
                                            </svg>
                                        </Link>
                                    </nav>)}
                        </div>
                    </section>
                    {isEbpcJudge
                        && (
                            <aside className="col margin-1-t-lg-down margin-3-t-lg">
                                <div className="card card-offscreen-x-lg-down">
                                    <p className="body-4">
                                        Feel free to browse all applicants.
                                        When you’re done, review your assigned ventures.
                                    </p>
                                    <div className="margin-t">
                                        <Link
                                            className="btn btn-secondary"
                                            to="/dashboard"
                                            style={{
                                                paddingTop: 15,
                                                paddingBottom: 15
                                            }}
                                        >
                                            View my assigned ventures
                                        </Link>
                                    </div>
                                </div>
                            </aside>)}
                </div>
            </div>
        </div>
    )
}
